import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Calendar, Users, Heart, Award, ArrowRight, Sparkles, Info, Image as ImageIcon } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { MandalaCorner } from '../../components/luxury/MandalaCorner';
import { PatolaBorder } from '../../components/luxury/PatolaBorder';
import { FloatingDiya } from '../../components/luxury/FloatingDiya';
import { SwastikaDecor } from '../../components/luxury/SwastikaDecor';
import { api } from '../../App';
import { broadcastHearts, onHeartsChange } from '../../lib/heartSync';
import { usePageContent, PreviewBanner } from '../../lib/content';
import { Megaphone } from 'lucide-react';
import { FeaturedEventBanner } from '../../components/promo/FeaturedEventBanner';

const fadeInUp = {
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: '-80px' },
  transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] }
};

const staggerContainer = {
  initial: {},
  whileInView: { transition: { staggerChildren: 0.1 } },
  viewport: { once: true }
};

// Heritage images for slideshow
const HERITAGE_IMAGES = [
  {
    url: "https://customer-assets-0z36b82j.emergentagent.net/job_notts-gujarati/artifacts/o7oouhod_Somnath%20Temple.png",
    title: "Somnath Temple",
    subtitle: "Sacred shrine of Gujarat",
    alt: "Somnath Temple at sunset"
  },
  {
    url: "https://customer-assets-0z36b82j.emergentagent.net/job_notts-gujarati/artifacts/uwif7i8x_Rani%20Ni%20vav.png",
    title: "Rani Ki Vav",
    subtitle: "UNESCO World Heritage stepwell",
    alt: "Rani Ki Vav in Patan, Gujarat"
  },
  {
    url: "https://customer-assets-0z36b82j.emergentagent.net/job_notts-gujarati/artifacts/prfxc3d6_rann-utsav.webp",
    title: "Rann of Kutch",
    subtitle: "Gujarat's white desert",
    alt: "Rann of Kutch with camels"
  },
  {
    url: "https://customer-assets-0z36b82j.emergentagent.net/job_notts-gujarati/artifacts/xgyycy7r_Statue%20Of%20Unity.webp",
    title: "Statue of Unity",
    subtitle: "World's tallest statue",
    alt: "Statue of Unity - Sardar Vallabhbhai Patel"
  },
  {
    url: "https://customer-assets-0z36b82j.emergentagent.net/job_notts-gujarati/artifacts/3byypjm2_Sardar%20Patel.webp",
    title: "Statue of Unity",
    subtitle: "Pride of Gujarat",
    alt: "Statue of Unity side view"
  }
];

export default function LuxuryHome() {
  const { t, c, isPreview } = usePageContent('home');
  const customImages = Array.isArray(c.hero_images) && c.hero_images.filter(i => i && i.url).length ? c.hero_images.filter(i => i.url).map(i => ({ url: i.url, alt: i.title || 'Slideshow image', title: i.title || '', subtitle: i.subtitle || '' })) : null;
  // Festival hero photos (Admin → Settings → Festival hero photos) take over while a festival theme is on
  const [festivalSlides, setFestivalSlides] = useState(null);
  useEffect(() => {
    api.get('/api/public/hero-slides').then(d => {
      const BACKEND = process.env.REACT_APP_BACKEND_URL || '';
      setFestivalSlides(d?.slides?.length ? d.slides.map(sl => ({ ...sl, url: sl.url.startsWith('/') ? `${BACKEND}${sl.url}` : sl.url })) : null);
    }).catch(() => setFestivalSlides(null));
  }, []);
  const SLIDES = festivalSlides || customImages || HERITAGE_IMAGES;
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [metrics, setMetrics] = useState({ visits: 0, hearts: 0 });
  const [heartPop, setHeartPop] = useState(false);

  // Record a site visit (once per browser session) and load counters
  useEffect(() => {
    const load = async () => {
      try {
        if (!sessionStorage.getItem('gs_visit_counted')) {
          sessionStorage.setItem('gs_visit_counted', '1');
          const res = await api.post('/api/metrics/visit', {});
          const m = await api.get('/api/metrics');
          setMetrics({ visits: res.visits ?? m.visits, hearts: m.hearts });
        } else {
          const m = await api.get('/api/metrics');
          setMetrics(m);
        }
      } catch { /* counters are non-critical */ }
    };
    load();
    // Keep the home heart card in sync when the navbar heart is tapped
    return onHeartsChange((count) => setMetrics(m => ({ ...m, hearts: count })));
  }, []);

  const handleHeartTap = async () => {
    setHeartPop(true);
    setTimeout(() => setHeartPop(false), 400);
    setMetrics(m => { const next = m.hearts + 1; broadcastHearts(next); return { ...m, hearts: next }; });
    try {
      const res = await api.post('/api/metrics/heart', {});
      if (typeof res?.hearts === 'number') {
        setMetrics(m => ({ ...m, hearts: res.hearts }));
        broadcastHearts(res.hearts);
      }
    } catch {}
  };

  // Auto-rotate images every 3 seconds (pause on hover)
  useEffect(() => {
    if (isPaused) return;
    
    const interval = setInterval(() => {
      setCurrentImageIndex((prev) => (prev + 1) % SLIDES.length);
    }, 3000);
    return () => clearInterval(interval);
  }, [isPaused, SLIDES.length]);

  return (
    <div className="overflow-hidden">
      {isPreview && <PreviewBanner page="Home" />}
      {t('notice_enabled', false) && t('notice_text', '') && (
        <div className="bg-[hsl(var(--lux-gold-soft))] border-b border-[hsl(var(--lux-gold)/0.4)]" data-testid="home-notice-banner">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 flex flex-col sm:flex-row sm:items-center gap-2 text-[hsl(var(--lux-royal))]">
            <span className="flex items-center gap-2 font-medium text-sm sm:text-base"><Megaphone className="w-4 h-4 shrink-0" />{t('notice_text', '')}</span>
            {t('notice_link', '') && <a href={t('notice_link', '')} className="text-sm font-semibold underline underline-offset-2 hover:text-[hsl(var(--lux-gold))] sm:ml-auto">{t('notice_link_label', 'Find out more')}</a>}
          </div>
        </div>
      )}
      {/* Hero Section */}
      <section className="relative min-h-[85vh] flex items-center overflow-hidden lux-bandhani-dots">
        {/* Background Gradient Overlay */}
        <div className="absolute inset-0 bg-[radial-gradient(1200px_circle_at_20%_10%,rgba(212,175,55,0.18),transparent_55%),radial-gradient(900px_circle_at_80%_0%,rgba(31,111,235,0.18),transparent_60%)]" />
        
        {/* Decorative Elements */}
        <MandalaCorner position="top-left" className="opacity-30" />
        <MandalaCorner position="bottom-right" className="opacity-30" />
        <FloatingDiya className="top-20 right-10 hidden lg:block" />
        
        {/* Swastika - Middle Right */}
        <div className="absolute top-1/2 right-20 -translate-y-1/2 hidden lg:block" style={{ width: '64px', height: '64px', pointerEvents: 'none' }}>
          <motion.img
            initial={{ opacity: 0, rotate: -180 }}
            animate={{ opacity: 0.4, rotate: 0 }}
            transition={{ duration: 1.5, ease: 'easeOut' }}
            src="https://customer-assets-0z36b82j.emergentagent.net/job_notts-gujarati/artifacts/16uquhpu_swastika.jfif"
            alt="Swastika"
            className="w-full h-full object-contain"
            style={{ filter: 'drop-shadow(0 2px 8px rgba(212, 175, 55, 0.3))' }}
          />
        </div>
        
        {/* Swastika - Bottom Left */}
        <div className="absolute bottom-20 left-20 hidden lg:block" style={{ width: '96px', height: '96px', pointerEvents: 'none' }}>
          <motion.img
            initial={{ opacity: 0, rotate: -180 }}
            animate={{ opacity: 0.4, rotate: 0 }}
            transition={{ duration: 1.5, ease: 'easeOut' }}
            src="https://customer-assets-0z36b82j.emergentagent.net/job_notts-gujarati/artifacts/16uquhpu_swastika.jfif"
            alt="Swastika"
            className="w-full h-full object-contain"
            style={{ filter: 'drop-shadow(0 2px 8px rgba(212, 175, 55, 0.3))' }}
          />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left: Content */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="space-y-8"
            >
              <div>
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[hsl(var(--lux-royal-soft))] border border-[hsl(var(--lux-gold)/0.4)] mb-6"
                  data-testid="luxury-hero-welcome-badge"
                >
                  <Sparkles className="w-4 h-4 text-[hsl(var(--lux-gold))]" />
                  <span className="text-sm font-medium text-[hsl(var(--lux-royal))]">{t('hero_badge', 'Welcome to our community')}</span>
                </motion.div>

                <h1
                  className="text-5xl sm:text-6xl lg:text-7xl font-bold text-[hsl(var(--lux-royal))] mb-6 leading-tight"
                  style={{ fontFamily: 'Cormorant Garamond', letterSpacing: '-0.02em' }}
                  data-testid="luxury-hero-title"
                >
                  {t('hero_title', 'Gujarat Samaj')}<br />
                  <span className="text-[hsl(var(--lux-gold))]">{t('hero_title_accent', 'Nottingham')}</span>
                </h1>

                <p className="text-lg sm:text-xl text-[hsl(var(--lux-ink)/0.8)] leading-relaxed max-w-xl">
                  {t('hero_subtitle', 'Celebrating Gujarati culture and heritage in Nottingham. Join our vibrant community for events, festivals, and meaningful connections.')}
                </p>
              </div>

              <div className="flex flex-wrap gap-4">
                <Link to={t('hero_primary_link', '/events')}>
                  <Button
                    size="lg"
                    className="bg-[hsl(var(--lux-royal))] text-white hover:bg-[hsl(var(--lux-royal-2))] shadow-[var(--lux-shadow-1)] hover:shadow-[var(--lux-shadow-2)] transition-all px-8 py-6 text-lg"
                    data-testid="luxury-hero-events-button"
                  >
                    <Calendar className="w-5 h-5 mr-2" />
                    {t('hero_primary_label', 'Browse Events')}
                  </Button>
                </Link>
                <Link to={t('hero_secondary_link', '/membership')}>
                  <Button
                    size="lg"
                    variant="outline"
                    className="border-[hsl(var(--lux-gold)/0.55)] text-[hsl(var(--lux-royal))] hover:bg-[hsl(var(--lux-gold-soft))] transition-all px-8 py-6 text-lg"
                    data-testid="luxury-hero-join-button"
                  >
                    <Users className="w-5 h-5 mr-2" />
                    {t('hero_secondary_label', 'Join Membership')}
                  </Button>
                </Link>
              </div>
            </motion.div>

            {/* Right: Heritage & Pride Slideshow */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, delay: 0.4 }}
              className="relative"
              onMouseEnter={() => setIsPaused(true)}
              onMouseLeave={() => setIsPaused(false)}
            >
              <div className="lux-ornate-card overflow-hidden aspect-[4/3] relative">
                <AnimatePresence mode="wait">
                  <motion.img
                    key={currentImageIndex}
                    src={(SLIDES[currentImageIndex] || SLIDES[0]).url}
                    alt={(SLIDES[currentImageIndex] || SLIDES[0]).alt}
                    className="w-full h-full object-cover"
                    style={{
                      objectPosition: (SLIDES[currentImageIndex] || SLIDES[0]).title === "Statue of Unity" ? 'center 20%' : 'center center'
                    }}
                    data-testid="luxury-hero-image"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.5 }}
                  />
                </AnimatePresence>
                <div className="absolute inset-0 bg-gradient-to-t from-[hsl(var(--lux-midnight)/0.4)] via-transparent to-transparent" />
                
                {/* Overlay text */}
                <div className="absolute bottom-6 left-6 right-6">
                  <AnimatePresence mode="wait">
                    <motion.div
                      key={currentImageIndex}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      transition={{ duration: 0.5 }}
                    >
                      <h3 className="text-white text-2xl font-bold mb-1" style={{ fontFamily: 'Cormorant Garamond' }}>
                        {(SLIDES[currentImageIndex] || SLIDES[0]).title}
                      </h3>
                      <p className="text-white/80 text-sm">{(SLIDES[currentImageIndex] || SLIDES[0]).subtitle}</p>
                    </motion.div>
                  </AnimatePresence>
                </div>
                
                {/* Slideshow indicators */}
                <div className="absolute bottom-3 right-6 flex gap-1.5">
                  {SLIDES.map((_, idx) => (
                    <button
                      key={idx}
                      onClick={() => setCurrentImageIndex(idx)}
                      className={`w-2 h-2 rounded-full transition-all ${
                        idx === currentImageIndex
                          ? 'bg-white w-6'
                          : 'bg-white/50 hover:bg-white/70'
                      }`}
                      aria-label={`Go to slide ${idx + 1}`}
                    />
                  ))}
                </div>
              </div>
              <PatolaBorder className="mt-3" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Gold Divider */}
      <div className="lux-gold-divider" />

      {/* Featured event billboard (flyer + dates + Buy tickets). Admin: Events -> Promote */}
      <FeaturedEventBanner />

      {/* Community Love — tappable heart counter (just below slideshow) */}
      {t('show_hearts', true) && (
      <section className="py-10 bg-white" data-testid="community-pulse-section">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
          <Card className="lux-ornate-card border-2 border-[hsl(var(--lux-gold)/0.5)]" data-testid="heart-counter-card">
            <CardContent className="p-6 flex items-center justify-center gap-6">
              <button
                onClick={handleHeartTap}
                aria-label="Tap the heart to show your love"
                className="w-16 h-16 rounded-full bg-[hsl(var(--lux-vermilion)/0.12)] flex items-center justify-center flex-shrink-0 cursor-pointer ring-2 ring-[hsl(var(--lux-vermilion)/0.35)] hover:ring-[hsl(var(--lux-vermilion))] hover:bg-[hsl(var(--lux-vermilion)/0.2)] active:scale-90 transition-all animate-pulse hover:animate-none"
                data-testid="heart-tap-button"
              >
                <motion.div animate={heartPop ? { scale: [1, 1.6, 1] } : {}} transition={{ duration: 0.4 }}>
                  <Heart className="w-8 h-8 text-[hsl(var(--lux-vermilion))]" fill="currentColor" />
                </motion.div>
              </button>
              <div>
                <div className="text-4xl font-bold text-[hsl(var(--lux-vermilion))] tabular-nums" style={{ fontFamily: 'Cormorant Garamond' }} data-testid="heart-counter-value">
                  {metrics.hearts.toLocaleString()}
                </div>
                <div className="text-sm text-[hsl(var(--lux-ink)/0.65)]">
                  {c.hearts_text ? <span className="font-semibold text-[hsl(var(--lux-vermilion))]">{c.hearts_text}</span> : <>Hearts from our community — <span className="font-semibold text-[hsl(var(--lux-vermilion))]">tap the heart to add yours</span></>}
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>
      )}

      {/* Highlights Section - Bento Grid */}
      <motion.section
        {...fadeInUp}
        className="py-20 bg-[hsl(var(--lux-parchment))]"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div {...staggerContainer} className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* About Us */}
            <motion.div {...fadeInUp}>
              <Link to="/about">
                <Card className="lux-ornate-card hover:shadow-[var(--lux-shadow-2)] transition-all h-full group cursor-pointer" data-testid="luxury-home-about-card">
                  <CardContent className="p-6 flex flex-col h-full">
                    <div className="flex items-start justify-between mb-4">
                      <div className="p-3 rounded-xl bg-[hsl(var(--lux-royal-soft))] group-hover:bg-[hsl(var(--lux-royal))] transition-colors">
                        <Info className="w-6 h-6 text-[hsl(var(--lux-royal))] group-hover:text-white transition-colors" />
                      </div>
                    </div>
                    <h3 className="text-2xl font-bold text-[hsl(var(--lux-royal))] mb-2" style={{ fontFamily: 'Cormorant Garamond' }}>
                      {t('about_card_title', 'About Us')}
                    </h3>
                    <p className="text-[hsl(var(--lux-ink)/0.7)] mb-4 flex-1">
                      {t('about_card_text', 'Discover our story, mission and the people behind Gujarat Samaj Nottingham.')}
                    </p>
                    <div className="flex items-center text-[hsl(var(--lux-gold))] font-medium group-hover:gap-2 transition-all">
                      Learn More <ArrowRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" />
                    </div>
                  </CardContent>
                </Card>
              </Link>
            </motion.div>

            {/* Saturday Club */}
            <motion.div {...fadeInUp}>
              <Link to="/saturday-club">
                <Card className="lux-ornate-card hover:shadow-[var(--lux-shadow-2)] transition-all h-full group cursor-pointer" data-testid="luxury-home-saturday-club-card">
                  <CardContent className="p-6 flex flex-col h-full">
                    <div className="flex items-start justify-between mb-4">
                      <div className="p-3 rounded-xl bg-[hsl(var(--lux-royal-soft))] group-hover:bg-[hsl(var(--lux-royal))] transition-colors">
                        <Calendar className="w-6 h-6 text-[hsl(var(--lux-royal))] group-hover:text-white transition-colors" />
                      </div>
                      <Badge variant="outline" className="border-[hsl(var(--lux-gold)/0.6)]">{t('club_card_badge', 'Every Saturday')}</Badge>
                    </div>
                    <h3 className="text-2xl font-bold text-[hsl(var(--lux-royal))] mb-2" style={{ fontFamily: 'Cormorant Garamond' }}>
                      {t('club_card_title', 'Saturday Club')}
                    </h3>
                    <p className="text-[hsl(var(--lux-ink)/0.7)] mb-4 flex-1">
                      {t('club_card_text', 'Gujarati language, culture and fun activities for children — £51 per term or £150 yearly.')}
                    </p>
                    <div className="flex items-center text-[hsl(var(--lux-gold))] font-medium group-hover:gap-2 transition-all">
                      Register Now <ArrowRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" />
                    </div>
                  </CardContent>
                </Card>
              </Link>
            </motion.div>

            {/* Gallery */}
            <motion.div {...fadeInUp}>
              <Link to="/gallery">
                <Card className="lux-ornate-card hover:shadow-[var(--lux-shadow-2)] transition-all h-full group cursor-pointer" data-testid="luxury-home-gallery-card">
                  <CardContent className="p-6 flex flex-col h-full">
                    <div className="flex items-start justify-between mb-4">
                      <div className="p-3 rounded-xl bg-[hsl(var(--lux-royal-soft))] group-hover:bg-[hsl(var(--lux-royal))] transition-colors">
                        <ImageIcon className="w-6 h-6 text-[hsl(var(--lux-royal))] group-hover:text-white transition-colors" />
                      </div>
                    </div>
                    <h3 className="text-2xl font-bold text-[hsl(var(--lux-royal))] mb-2" style={{ fontFamily: 'Cormorant Garamond' }}>
                      {t('gallery_card_title', 'Gallery')}
                    </h3>
                    <p className="text-[hsl(var(--lux-ink)/0.7)] mb-4 flex-1">
                      {t('gallery_card_text', 'Relive our celebrations — photos and memories from community events and festivals.')}
                    </p>
                    <div className="flex items-center text-[hsl(var(--lux-gold))] font-medium group-hover:gap-2 transition-all">
                      View Gallery <ArrowRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" />
                    </div>
                  </CardContent>
                </Card>
              </Link>
            </motion.div>
          </motion.div>

          {/* Sponsors Preview */}
          {t('show_sponsors_card', true) && (
          <motion.div {...fadeInUp} className="mt-6">
            <Link to="/sponsors">
              <Card className="lux-ornate-card hover:shadow-[var(--lux-shadow-2)] transition-all group cursor-pointer">
                <CardContent className="p-6 flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="p-3 rounded-xl bg-[hsl(var(--lux-royal-soft))] group-hover:bg-[hsl(var(--lux-royal))] transition-colors">
                      <Award className="w-6 h-6 text-[hsl(var(--lux-royal))] group-hover:text-white transition-colors" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-[hsl(var(--lux-royal))]" style={{ fontFamily: 'Cormorant Garamond' }}>
                        {t('sponsors_card_title', 'Our Sponsors')}
                      </h3>
                      <p className="text-sm text-[hsl(var(--lux-ink)/0.7)]">
                        {t('sponsors_card_text', 'Meet our valued partners and supporters')}
                      </p>
                    </div>
                  </div>
                  <ArrowRight className="w-5 h-5 text-[hsl(var(--lux-gold))] group-hover:translate-x-1 transition-transform" />
                </CardContent>
              </Card>
            </Link>
          </motion.div>
          )}
        </div>
      </motion.section>
    </div>
  );
}
